import { createContext, useContext } from 'react'
import type { LoginCodeChallenge } from '@/entities/session/model/loginChallenge'
import type { Session } from '@/entities/session/model/session'
import type { AcceptInviteRequestV4 } from '@/shared/api/generated/models/AcceptInviteRequestV4'
import type { MeChangePasswordStatus200 } from '@/shared/api/generated/models/MeChangePassword'
import type { MyInternshipV4 } from '@/shared/api/generated/models/MyInternshipV4'
import type { MyRoleV4 } from '@/shared/api/generated/models/MyRoleV4'
import type { OrderTypeNameKey } from '@/shared/api/generated/models/OrderTypeName'
import type { ReadEventResponseV4 } from '@/shared/api/generated/models/ReadEventResponseV4'
import type { RoleTypeAdvancedNameKey } from '@/shared/api/generated/models/RoleTypeAdvancedName'

export type SessionContextValue = {
  status: 'restoring' | 'authenticated' | 'anonymous'
  session: Session | undefined
  isSwitchingContext: boolean
  isTransitioningSupportSession: boolean
  isPasswordChangeRequired: boolean
  isTermsAcceptanceRequired: boolean
  login: (email: string, password: string) => Promise<LoginCodeChallenge | undefined>
  verifyLoginCode: (pendingToken: string, code: string, rememberDevice: boolean) => Promise<void>
  completePasswordChange: (payload: MeChangePasswordStatus200) => Promise<void>
  completeTermsAcceptance: () => Promise<void>
  refreshUser: () => Promise<void>
  refreshContext: () => Promise<void>
  refreshPages: () => Promise<void>
  logout: () => Promise<void>
  resetPassword: (token: string, newPassword: string) => Promise<void>
  acceptInvite: (request: AcceptInviteRequestV4) => Promise<RoleTypeAdvancedNameKey>
  startSupportSession: (userId: string, caseNumber: string) => Promise<void>
  stopSupportSession: () => Promise<void>
  switchRoleContext: (context: MyRoleV4) => Promise<void>
  switchInternshipContext: (internship: MyInternshipV4) => Promise<void>
  acceptOrgInvite: (inviteId: string) => Promise<void>
  completePayment: (orderReference: string, paymentReference: string) => Promise<OrderTypeNameKey>
  openNotificationEvent: (eventId: string) => Promise<ReadEventResponseV4>
}

export const SessionContext = createContext<SessionContextValue | undefined>(undefined)

export const useSession = () => {
  const value = useContext(SessionContext)
  if (!value) {
    throw new Error('useSession must be used within SessionProvider')
  }
  return value
}
